import type { RouteLocationRaw, Router } from "vue-router";
import type { MfaRequired, OktaBody } from "@/api/auth.api";
import type { OktaCredentials } from "@/api/okta-integration.api";
import type { LoginCredentials, LoginResponse } from "@/schemas/login.schema";
import type { SignupCredentials } from "@/schemas/signup.schema";
import { useQueryCache } from "@pinia/colada";
import * as Sentry from "@sentry/vue";
import { acceptHMRUpdate, defineStore } from "pinia";
import { readonly, ref } from "vue";
import { isNavigationFailure, useRouter } from "vue-router";
import rest from "@/api/rest";
import { closeModal } from "@/components/providers/DialogProvider";
import { toaster } from "@/components/providers/ToasterProvider";
import { openVerifyMfa } from "@/components/VerifyMfa";
import { api, clearProactiveRefresh, scheduleProactiveRefresh } from "@/services/api";
import { emitter } from "@/services/emitter";
import { $t } from "@/services/i18n";
import { trackSignupAfterSuccess } from "@/services/signup-attribution";
import { notifyError } from "@/utils/errors";
import { useUserProfile } from "./user-profile.store";

type MfaPayload =
  | {
      type: "basic";
      credentials: LoginCredentials;
    }
  | {
      type: "google" | "microsoft";
      token: string;
    };

async function firstAccessibleMenuRoute(router: Router): Promise<RouteLocationRaw> {
  for (const route of router.getRoutes()) {
    if (route.meta.navigation !== "menu") continue;
    if (route.meta.condition && !(await route.meta.condition())) continue;

    return typeof route.redirect === "string" ? route.redirect : route.path;
  }

  return "/";
}

async function organizationSwitchDestination(router: Router): Promise<RouteLocationRaw | null> {
  const current = router.currentRoute.value;

  if (current.meta.projectSwitchProtection) {
    const parentName = current.meta.parent;

    if (typeof parentName === "string") {
      const parent = router.getRoutes().find(route => route.name === parentName);

      if (parent && (!parent.meta.condition || (await parent.meta.condition()))) {
        return { name: parentName };
      }
    }

    return firstAccessibleMenuRoute(router);
  }

  if (current.meta.condition && !(await current.meta.condition())) {
    return firstAccessibleMenuRoute(router);
  }

  return null;
}

export const useAuth = defineStore("auth", () => {
  const router = useRouter();

  const redirect = router.currentRoute.value.query.redirect as string;

  const firstPage = redirect ?? "/api-keys";

  const token = ref<string | null>(null);
  const isSwitchingOrganization = ref(false);
  const oktaCredentials = ref<OktaCredentials | null>(null);
  const userStore = useUserProfile();
  const queryCache = useQueryCache();

  async function getOktaCredentials() {
    const hostParts = globalThis.location.host.split(".");

    if (hostParts[1] === "org") {
      const organization = hostParts[0];
      const res = await rest.oktaIntegration.getCredentials(organization);

      if (res) {
        oktaCredentials.value = res;
      }
    }
  }

  const handleMfa =
    (payload: MfaPayload) =>
    (loginResponse: LoginResponse | MfaRequired): Promise<LoginResponse | void> => {
      if ("mfa_required" in loginResponse) {
        return new Promise((resolve) => {
          openVerifyMfa({
            email: loginResponse.email,
            language: loginResponse.language,
            full_name: loginResponse.full_name,
            btnLabel: $t("login"),
            onClose: resolve,
            onSubmit: async (verification) => {
              let res: LoginResponse | MfaRequired;

              if (payload.type === "basic") {
                res = await rest.auth
                  .login({
                    ...payload.credentials,
                    mfa: verification,
                  })
                  .catch(notifyError);
              } else if (payload.type === "google") {
                res = await rest.auth
                  .googleLogin({
                    token: payload.token,
                    mfa: verification,
                  })
                  .catch(notifyError);
              } else if (payload.type === "microsoft") {
                res = await rest.auth
                  .microsoftLogin({
                    token: payload.token,
                    mfa: verification,
                  })
                  .catch(notifyError);
              } else {
                throw new Error("Invalid MFA type");
              }

              if ("mfa_required" in res) {
                toaster.error("Invalid MFA code");
                return;
              }

              resolve(res);
              closeModal();
            },
          });
          return { ...loginResponse };
        });
      }

      return Promise.resolve(loginResponse);
    };

  async function login(credentials: LoginCredentials) {
    const response: LoginResponse | void = await rest.auth
      .login(credentials)
      .then(handleMfa({ type: "basic", credentials }));

    if (!response) return;

    token.value = response.access_token;

    assignToken(response.access_token);
    await getUser();
  }

  async function googleLogin(googleToken: string) {
    const response = await rest.auth
      .googleLogin({ token: googleToken })
      .then(handleMfa({ type: "google", token: googleToken }));

    if (!response) return;

    token.value = response.access_token;

    assignToken(response.access_token);
    await getUser();
  }

  async function microsoftLogin(microsoftToken: string) {
    const response = await rest.auth
      .microsoftLogin({ token: microsoftToken })
      .then(handleMfa({ type: "microsoft", token: microsoftToken }));

    if (!response) return;

    token.value = response.access_token;

    assignToken(response.access_token);
    await getUser();
  }

  async function oktaLogin(body: OktaBody) {
    const response = await rest.auth.oktaLogin(body);

    if (!response) return;

    token.value = response.access_token;

    assignToken(response.access_token);
    await getUser();
  }

  async function signUp(credentials: SignupCredentials, access_code: string) {
    const response = await trackSignupAfterSuccess(
      rest.auth.register(credentials, access_code)
    ).catch(notifyError);

    token.value = response.access_token;

    assignToken(response.access_token);
    await getUser();

    router.push(firstPage);
  }

  async function logout() {
    await rest.auth.logout();
    clearProactiveRefresh();
    delete api.defaults.headers.common["Authorization"];
    token.value = null;
    Sentry.setUser(null);
    router.push("/login");
    emitter.emit("logout");
    location.reload();
  }

  async function checkToken() {
    if (token.value) {
      assignToken(token.value);
      return;
    }

    const access_token = await rest.auth.refresh();

    if (access_token) {
      token.value = access_token;
      assignToken(access_token);
      await getUser();
    }
  }

  function assignToken(token: string) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    scheduleProactiveRefresh(token);
  }

  let getUserPromise: Promise<void> | null = null;

  async function getUser(retries = 3) {
    if (getUserPromise) return getUserPromise;

    getUserPromise = _getUser(retries).finally(() => {
      getUserPromise = null;
    });

    return getUserPromise;
  }

  async function _getUser(retries = 3) {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const user = await rest.auth.me();
        userStore.setUser(user);
        // Contexto p/ triagem no Sentry (no-op seguro quando o SDK não inicializou).
        Sentry.setUser({ id: user.user_id, email: user.email, username: user.full_name });
        // `key` só existe no /me a partir do AI-994 (server); id é o fallback.
        Sentry.setTag("organization", user.organization?.key ?? user.organization?.id);
        Sentry.setTag("role", user.role);
        return;
      } catch (_error) {
        if (attempt < retries) {
          await new Promise((r) => setTimeout(r, attempt * 1000));
          continue;
        }
        clearProactiveRefresh();
        delete api.defaults.headers.common["Authorization"];
        token.value = null;
        toaster.error($t("error_loading_profile"));
        await rest.auth.logout().catch(() => {});
        router.push("/login");
      }
    }
  }

  async function refreshUser() {
    const token = await rest.auth.refresh({ refreshUser: true });
    assignToken(token);
    await getUser();
  }

  async function switchOrganization(organizationId: string) {
    // Leave entity-bound pages while their data still belongs to the current
    // organization. Existing leave guards can then save, disconnect, or cancel
    // the switch without being hidden behind the organization-switch overlay.
    const beforeSwitchDestination = await organizationSwitchDestination(router);
    if (beforeSwitchDestination) {
      const navigationFailure = await router.replace(beforeSwitchDestination);
      if (isNavigationFailure(navigationFailure)) return;
    }

    isSwitchingOrganization.value = true;
    try {
      const response = await rest.auth.switchOrganization(organizationId);

      token.value = response.access_token;
      assignToken(response.access_token);

      // Reset project selection before reloading profile — prevents stale
      // project ID from old org being sent in queries during the transition.
      // Lazy import to avoid circular dependency in Vite's production bundle.
      const { ALL_PROJECTS_ID, useProjectStore } = await import("./project.store");
      useProjectStore().selectedProjectId = ALL_PROJECTS_ID;

      // Reload user profile (will get new org context + permissions)
      await getUser();

      const destination = await organizationSwitchDestination(router);
      if (destination) await router.replace(destination);

      // Invalidate after leaving context-bound routes so refetches cannot use
      // entity IDs that belong to the previous organization.
      await queryCache.invalidateQueries();
    } finally {
      isSwitchingOrganization.value = false;
    }
  }

  async function createOrganization(body: { name: string; currency?: string }) {
    const newOrg = await rest.auth.createOrganization(body);

    // Auto-switch to the new organization
    await switchOrganization(newOrg.id);

    return newOrg;
  }

  async function leaveOrganization(orgId: string) {
    const result = await rest.auth.leaveOrganization(orgId);

    // The server invalidates the current JWT after leave, so we can't use
    // switchOrganization (which needs a valid token). Instead, refresh the
    // session — the server will resolve to the next active org automatically.
    try {
      // Reset project selection to prevent stale project ID from old org
      const { ALL_PROJECTS_ID, useProjectStore } = await import("./project.store");
      useProjectStore().selectedProjectId = ALL_PROJECTS_ID;

      await refreshUser();
      queryCache.invalidateQueries();
      // Navigate to home so the user sees the new org context clearly
      router.push("/");
    } catch {
      // If refresh fails (no active orgs left), logout
      await logout();
    }

    return result;
  }

  async function deleteOrganization(orgId: string) {
    await rest.auth.deleteOrganization(orgId);

    // Server soft-deleted the org and invalidated sessions.
    // Try to refresh into another org, or logout if none left.
    try {
      const { ALL_PROJECTS_ID, useProjectStore } = await import("./project.store");
      useProjectStore().selectedProjectId = ALL_PROJECTS_ID;

      await refreshUser();
      queryCache.invalidateQueries();
      router.push("/");
    } catch {
      await logout();
    }
  }

  return {
    token: readonly(token),
    setToken: (newToken: string) => {
      assignToken(newToken);
      token.value = newToken;
    },
    login,
    googleLogin,
    microsoftLogin,
    oktaLogin,
    signUp,
    logout,
    checkToken,
    refreshUser,
    isSwitchingOrganization: readonly(isSwitchingOrganization),
    switchOrganization,
    createOrganization,
    leaveOrganization,
    deleteOrganization,
    oktaCredentials,
    getOktaCredentials,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuth, import.meta.hot));
}
